import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Divider,
  Row,
  Col,
  Form,
  Card,
  Select,
  Tooltip,
  InputNumber,
  FormInstance,
  Alert,
  Radio,
  Typography,
} from "antd";
import { RFMType, RFMTier, RFMTierDescriptions } from "../../constants/enums";
import { BrandRFMProfile } from "../../models/brandRFMProfile";
import {
  BrandRFMSetting,
  DefaultBrandRFMSetting,
} from "../../models/brandRFMSetting";

interface ModalProps {
  visible: boolean;
  onCancel: () => void;
  form: FormInstance;
  onFinish: (values: any) => void;
  initialValues: any;
  onValuesChange?: (changed: any, all: any) => void;

  activeTab: "Recency" | "Frequency" | "Monetary";
  setActiveTab: (val: "Recency" | "Frequency" | "Monetary") => void;

  brandRFMSetting: any[];
  updatedBrandRFMSetting: any[];
  defaultSetting: DefaultBrandRFMSetting;
  tooltipTexts?: Record<string, string>;

  isTyping: boolean;
  setIsTyping: (val: boolean) => void;
  isTypingEndValue: boolean;
  setIsTypingEndValue: (val: boolean) => void;

  cutOffDateInEdit?: string;
  handleChangeCutOffDateInEdit?: (val: string) => void;
  optionsCutOffDateOnEdit?: { label: string; value: string }[];

  isMobile?: boolean;
  loading?: boolean;

  resetEditForm: (resetToDefault: boolean) => void;
  setIsConfirmationModalVisible: (val: boolean) => void;
  selectedPeriod?: boolean;
  selectedBrandRFMProfile?: BrandRFMProfile;
  setSelectedBrandRFMProfile?: (profile: BrandRFMProfile) => void;
}

const CriteriaForm: React.FC<ModalProps> = ({
  visible,
  onCancel,
  form,
  onFinish,
  initialValues,
  onValuesChange,
  activeTab,
  setActiveTab,
  brandRFMSetting,
  updatedBrandRFMSetting,
  defaultSetting,
  tooltipTexts,
  isTyping,
  setIsTyping,
  isTypingEndValue,
  setIsTypingEndValue,
  cutOffDateInEdit,
  handleChangeCutOffDateInEdit,
  optionsCutOffDateOnEdit,
  isMobile,
  loading,
  resetEditForm,
  setIsConfirmationModalVisible,
  selectedPeriod,
  selectedBrandRFMProfile,
  setSelectedBrandRFMProfile,
}) => {
  const { Option } = Select;
  const { Text } = Typography;
  const [defaultProfileSetting, setDefaultProfileSetting] = useState<
    any[] | null
  >(null);
  const [manualFormBackup, setManualFormBackup] = useState<any>(null);

  const [isAuto, setIsAuto] = useState(false);
  useEffect(() => {
    const loadDefaultProfiles = async () => {
      const profile1 = await makeDefaultProfile(defaultSetting?.FullService, {
        Id: 0,
        Name: "Full Service Default Profiles",
      });

      const profile2 = await makeDefaultProfile(defaultSetting?.QuickService, {
        Id: -1,
        Name: "Quick Service Default Profiles",
      });

      setDefaultProfileSetting([profile1, profile2]);
    };

    if (defaultSetting) {
      loadDefaultProfiles();
    }
  }, [defaultSetting]);

  const makeDefaultProfile = useCallback(
    async (
      brandRFMProfileSetting: BrandRFMSetting[],
      overrides: Partial<{
        Id: number;
        Name: string;
        Description: string;
      }> = {}
    ) => {
      const settings = Array.isArray(brandRFMProfileSetting)
        ? brandRFMProfileSetting
        : [];

      return {
        Id: overrides.Id ?? 0,
        BrandId: selectedBrandRFMProfile?.BrandId,
        Name: overrides.Name ?? "Full Service Default Profiles",
        Description: overrides.Description ?? "Full Service Default Profiles",
        CreatedDate: new Date().toISOString(),
        BrandRFMProfileSetting: settings,
      };
    },
    [selectedBrandRFMProfile]
  );
  const applyDefaultProfile = (profileName: string) => {
    const matchedProfile = defaultProfileSetting?.find(
      (p) => p?.Name === profileName
    );

    if (!matchedProfile) return;

    const fields: Record<string, any> = {};
    matchedProfile.BrandRFMProfileSetting?.forEach((item: any) => {
      const keyStart = `${item.RFMType}-${item.RFMTier}-start`;
      const keyEnd = `${item.RFMType}-${item.RFMTier}-end`;
      fields[keyStart] = item.RFMStartValue;
      fields[keyEnd] = item.RFMEndValue;
    });

    form.setFieldsValue(fields);
  };
  useEffect(() => {
    if (!defaultProfileSetting || !selectedBrandRFMProfile) return;

    if (selectedBrandRFMProfile.IsAuto) {
      setIsAuto(true);

      const activeProfileName =
        selectedBrandRFMProfile?.Name || "Full Service Default Profiles";

      applyDefaultProfile(activeProfileName);
    }
  }, [defaultProfileSetting]);

  useEffect(() => {
    if (selectedBrandRFMProfile && !manualFormBackup) {
      setManualFormBackup(form.getFieldsValue(true));
    }
  }, [selectedBrandRFMProfile]);

  return (
    <>
      <Row className="rfm-tabs" style={{ columnGap: "10px" }}>
        <Button
          size="large"
          onClick={() => {
            setActiveTab("Recency");
            setIsTyping(false);
          }}
          type={activeTab === "Recency" ? "primary" : "default"}
        >
          Recency
        </Button>
        <Button
          size="large"
          onClick={() => {
            setActiveTab("Frequency");
            setIsTyping(false);
          }}
          type={activeTab === "Frequency" ? "primary" : "default"}
        >
          Frequency
        </Button>
        <Button
          size="large"
          onClick={() => {
            setActiveTab("Monetary");
            setIsTyping(false);
          }}
          type={activeTab === "Monetary" ? "primary" : "default"}
        >
          Monetary
        </Button>
      </Row>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={initialValues}
        onValuesChange={onValuesChange}
      >
        <Card
          title={
            <span style={{ fontSize: "16px", fontWeight: "bold" }}>
              {activeTab}
            </span>
          }
          className="card-criteria-table"
          variant="borderless"
          type="inner"
          style={{ width: "100%", marginTop: isMobile ? 5 : 25 }}
        >
          {["Recency", "Frequency", "Monetary"].map((section) => {
            const isFrequency = section === "Frequency";

            return (
              <div
                key={section}
                className="rfm-section"
                style={{ display: activeTab === section ? "block" : "none" }}
              >
                {isFrequency && (
                  <Row
                    gutter={[16, 8]}
                    className="rfm-row"
                    align="middle"
                    justify="space-between"
                  >
                    <Col xs={24} md={6} xl={6}>
                      <span style={{ fontSize: 14 }}>Selected Period</span>
                    </Col>
                    <Col xs={24} md={7} xl={7}>
                      <Select
                        size="large"
                        style={{ width: "100%", display: "block" }}
                        value={cutOffDateInEdit}
                        onChange={handleChangeCutOffDateInEdit}
                      >
                        {optionsCutOffDateOnEdit?.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col span={1}></Col>
                    <Col span={6}></Col>
                  </Row>
                )}
                {activeTab === "Monetary" && (
                  <Card
                    style={{
                      background: "rgba(0,0,0,0.02)",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      marginBottom: 10,
                      padding: "2px",
                    }}
                    bodyStyle={{
                      padding: "5px",
                    }}
                  >
                    <Row
                      align="middle"
                      justify="space-between"
                      gutter={[32, 0]}
                    >
                      {/* Left Label */}
                      <Col xs={24} md={6} xl={7}>
                        <Text strong style={{ fontSize: 14, color: "#1f2937" }}>
                          Calculation Mode
                        </Text>
                      </Col>

                      {/* Right Options */}
                      <Col xs={24} md={18} xl={17}>
                        <Radio.Group
                          className="w-100"
                          value={isAuto ? "auto" : "fixed"}
                          size="large"
                          onChange={(e) => {
                            const mode = e.target.value;

                            if (mode === "auto") {
                              if (!selectedBrandRFMProfile) return;

                              let newProfile = {
                                ...selectedBrandRFMProfile,
                                IsAuto: true,
                              };
                              const currentValues = form.getFieldsValue(true);
                              setManualFormBackup(currentValues);
                              setIsAuto(true);

                              const activeProfileName =
                                selectedBrandRFMProfile?.Name ===
                                  "Full Service Default Profiles" ||
                                selectedBrandRFMProfile?.Name ===
                                  "Quick Service Default Profiles"
                                  ? selectedBrandRFMProfile?.Name
                                  : "Full Service Default Profiles";

                              applyDefaultProfile(activeProfileName);
                              setSelectedBrandRFMProfile?.(newProfile);
                            } else {
                              setIsAuto(false);

                              if (manualFormBackup) {
                                form.setFieldsValue(manualFormBackup);
                              }

                              if (!selectedBrandRFMProfile) return;

                              let newProfile = {
                                ...selectedBrandRFMProfile,
                                IsAuto: false,
                              };
                              setSelectedBrandRFMProfile?.(newProfile);
                            }
                          }}
                        >
                          <Row gutter={[16, 0]} align="top">
                            {/* Auto */}
                            <Col xs={24} md={12} xl={12}>
                              <Radio
                                value="auto"
                                disabled={
                                  !(
                                    selectedBrandRFMProfile?.Name ===
                                      "Full Service Default Profiles" ||
                                    selectedBrandRFMProfile?.Name ===
                                      "Quick Service Default Profiles"
                                  )
                                }
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    opacity:
                                      selectedBrandRFMProfile?.Name ===
                                        "Full Service Default Profiles" ||
                                      selectedBrandRFMProfile?.Name ===
                                        "Quick Service Default Profiles"
                                        ? 1
                                        : 0.5,
                                  }}
                                >
                                  <Text strong>Auto (Dynamic)</Text>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 12 }}
                                  >
                                    Values may update daily
                                  </Text>
                                </div>
                              </Radio>
                            </Col>

                            {/* Fixed */}
                            <Col xs={24} md={12} xl={12}>
                              <Radio value="fixed">
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Text strong>Fixed (Manual Input)</Text>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 12 }}
                                  >
                                    Set your own static values manually
                                  </Text>
                                </div>
                              </Radio>
                            </Col>
                          </Row>
                        </Radio.Group>
                      </Col>
                    </Row>
                  </Card>
                )}
                {Object.values(RFMTier)
                  .filter((val) => typeof val === "number")
                  .sort((a, b) => (a as number) - (b as number))
                  .reverse()
                  .map((tierNumber, index) => {
                    const tier = tierNumber as number;
                    const rfmType = RFMType[section as keyof typeof RFMType];
                    const fieldStart = `${rfmType}-${tier}-start`;
                    const fieldEnd = `${rfmType}-${tier}-end`;
                    const startValue = form.getFieldValue(fieldStart);
                    const endValue = form.getFieldValue(fieldEnd);
                    return (
                      <Row
                        key={`${rfmType}-${tier}`}
                        gutter={[16, 8]}
                        align="middle"
                        justify="space-between"
                        className="rfm-row"
                      >
                        <Col xs={24} md={6} xl={6}>
                          <span style={{ fontSize: 14 }}>
                            {RFMTierDescriptions[tier as RFMTier] ?? "Unknown"}{" "}
                            Value
                          </span>
                        </Col>

                        <Col xs={10} md={6} xl={6}>
                          <Form.Item
                            name={fieldStart}
                            validateTrigger={["onBlur", "onChange"]}
                            style={{ marginBottom: 0 }}
                            rules={[
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  if (index === 0) {
                                    if (
                                      section === "Frequency" &&
                                      (value === "" ||
                                        value === undefined ||
                                        value === 0)
                                    ) {
                                      return Promise.resolve();
                                    }
                                    if (
                                      section === "Frequency" &&
                                      //  value <= 0 &&
                                      value < 0 &&
                                      (value !== "" || value !== undefined)
                                    ) {
                                      return Promise.reject(
                                        new Error(
                                          "Value must be greater than 0"
                                        )
                                      );
                                    }
                                    if (
                                      section === "Monetary" &&
                                      (value === undefined || value < 0)
                                    ) {
                                      if (index === 0) {
                                        return Promise.resolve();
                                      }
                                      return Promise.reject(
                                        new Error("Value cannot be negative")
                                      );
                                    }
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <InputNumber<string>
                              size="large"
                              step={
                                section === "Frequency"
                                  ? 0.1
                                  : section === "Recency"
                                  ? 1
                                  : 0.01
                              }
                              className="w-100"
                              decimalSeparator="."
                              onFocus={() => setIsTyping(false)}
                              onChange={() => setIsTyping(true)}
                              stringMode
                              readOnly={
                                !(
                                  index === 0 &&
                                  (section === "Monetary" ||
                                    section === "Frequency")
                                )
                              }
                              disabled={
                                !(
                                  index === 0 &&
                                  (section === "Monetary" ||
                                    section === "Frequency")
                                ) ||
                                (index === 0 &&
                                  section === "Monetary" &&
                                  selectedBrandRFMProfile?.IsAuto === true)
                              }
                              formatter={(value) => {
                                if (!value) return "";

                                if (section === "Frequency") {
                                  const num: any = parseFloat(
                                    value.toString().replace(/,/g, ".")
                                  );
                                  if (
                                    value === "0" &&
                                    !isTyping &&
                                    tier === RFMTier.VeryLow
                                  ) {
                                    return "";
                                  }

                                  return Number.isInteger(num)
                                    ? num.toString()
                                    : num.toFixed(1);
                                }
                                if (section === "Monetary") {
                                  const num = parseFloat(
                                    value.toString().replace(/,/g, ".")
                                  );
                                  if (
                                    value === "0" &&
                                    !isTyping &&
                                    tier === RFMTier.VeryLow
                                  ) {
                                    return "";
                                  }
                                  return Number.isInteger(num)
                                    ? num.toString()
                                    : num.toFixed(2);
                                }
                                return value.toString().replace(/,/g, ".");
                              }}
                              parser={(value) => {
                                if (!value) return "";
                                return value.replace(/,/g, ".");
                              }}
                              addonAfter={
                                startValue === null ||
                                startValue === undefined ||
                                startValue === "" ||
                                startValue === 0 ||
                                startValue === "0" ? (
                                  <Tooltip
                                    title={tooltipTexts?.[fieldEnd] || ""}
                                  >
                                    <span style={{ marginRight: 5 }}>
                                      {section === "Monetary"
                                        ? "$"
                                        : section === "Frequency"
                                        ? "Orders"
                                        : "Days"}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <span style={{ marginRight: 5 }}>
                                    {section === "Monetary"
                                      ? "$"
                                      : section === "Frequency"
                                      ? "Orders"
                                      : "Days"}
                                  </span>
                                )
                              }
                            />
                          </Form.Item>
                        </Col>

                        <Col
                          xs={3}
                          md={2}
                          xl={2}
                          style={{ textAlign: "center", fontSize: 16 }}
                        >
                          To
                        </Col>
                        <Col xs={10} md={6} xl={6}>
                          <Form.Item
                            name={`${rfmType}-${tier}-end`}
                            style={{ marginBottom: 0 }}
                            dependencies={[
                              `${rfmType}-${tier}-start`,
                              `${rfmType}-${(tier ?? 0) + 1}-start`,
                            ]}
                            rules={[
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const currentTier = tier ?? 0;
                                  const startValue = getFieldValue(
                                    `${rfmType}-${currentTier}-start`
                                  );
                                  if (
                                    (value === null ||
                                      value === undefined ||
                                      startValue === undefined) &&
                                    ((currentTier === RFMTier.VeryHigh &&
                                      rfmType === RFMType.Frequency) ||
                                      (currentTier === RFMTier.VeryLow &&
                                        rfmType === RFMType.Recency) ||
                                      (currentTier === RFMTier.VeryHigh &&
                                        rfmType === RFMType.Monetary))
                                  ) {
                                    return Promise.resolve();
                                  }

                                  if (rfmType === RFMType.Frequency) {
                                    if (
                                      value === undefined &&
                                      startValue === undefined
                                    ) {
                                      return Promise.reject(
                                        new Error(
                                          `End value must be greater than 0`
                                        )
                                      );
                                    }
                                    if (
                                      value <= startValue &&
                                      currentTier !== RFMTier.VeryLow &&
                                      currentTier !== RFMTier.VeryHigh
                                    ) {
                                      return Promise.reject(
                                        new Error(
                                          `End value must be greater than ${startValue}`
                                        )
                                      );
                                    }
                                    if (
                                      value < startValue &&
                                      (value === null || value === "0")
                                    ) {
                                      return Promise.reject(
                                        new Error(
                                          `End value must be greater than or equal to ${startValue}`
                                        )
                                      );
                                    }
                                  } else {
                                    if (
                                      value <= startValue &&
                                      rfmType === RFMType.Monetary &&
                                      currentTier === RFMTier.VeryHigh &&
                                      (value === "" ||
                                        value === null ||
                                        value === "0") &&
                                      !isTyping
                                    ) {
                                      return Promise.resolve();
                                    }
                                    if (
                                      (value === "" ||
                                        value === null ||
                                        value === "0" ||
                                        value === 0) &&
                                      (currentTier === RFMTier.VeryHigh ||
                                        currentTier === RFMTier.VeryLow)
                                    ) {
                                      return Promise.resolve();
                                    }
                                    if (value <= startValue) {
                                      return Promise.reject(
                                        new Error(
                                          `End value must be greater than ${startValue}`
                                        )
                                      );
                                    }
                                    if (
                                      value === null ||
                                      value === "0" ||
                                      value === undefined
                                    ) {
                                      return Promise.reject(
                                        new Error(
                                          `End value must be greater than 0`
                                        )
                                      );
                                    }
                                  }

                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <InputNumber<string>
                              size="large"
                              step={
                                section === "Frequency"
                                  ? 0.1
                                  : section === "Recency"
                                  ? 1
                                  : 0.01
                              }
                              className="w-100"
                              onFocus={() => {
                                setIsTypingEndValue(false);
                              }}
                              decimalSeparator="."
                              stringMode
                              formatter={(value) => {
                                if (!value) return "";
                                const num = parseFloat(
                                  value.toString().replace(/,/g, ".")
                                );

                                if (section === "Recency") {
                                  return Math.floor(num).toString();
                                }
                                if (section === "Frequency") {
                                  const num = parseFloat(
                                    value.toString().replace(/,/g, ".")
                                  );
                                  if (
                                    (value === "0" || value === null) &&
                                    !isTypingEndValue &&
                                    tier === RFMTier.VeryHigh
                                  )
                                    return "";

                                  const rounded = Math.round(num * 10) / 10;

                                  return Number.isInteger(rounded)
                                    ? rounded.toString()
                                    : rounded.toFixed(1);
                                }
                                if (section === "Monetary") {
                                  const num = parseFloat(
                                    value.toString().replace(/,/g, ".")
                                  );
                                  if (
                                    (value === "0" || value === null) &&
                                    !isTypingEndValue &&
                                    tier === RFMTier.VeryHigh
                                  ) {
                                    return "";
                                  }

                                  return Number.isInteger(num)
                                    ? num.toString()
                                    : num.toFixed(2);
                                }
                                return value.toString().replace(/,/g, ".");
                              }}
                              parser={(value) =>
                                value ? value.replace(/,/g, ".") : ""
                              }
                              disabled={
                                section === "Monetary"
                                  ? selectedBrandRFMProfile?.IsAuto
                                  : false
                              }
                              addonAfter={
                                endValue === null ||
                                endValue === "" ||
                                endValue === undefined ||
                                endValue === 0 ||
                                endValue === "0" ? (
                                  <Tooltip
                                    placement="topLeft"
                                    title={tooltipTexts?.[fieldStart] || ""}
                                  >
                                    <span style={{ marginRight: 5 }}>
                                      {section === "Monetary"
                                        ? "$"
                                        : section === "Frequency"
                                        ? "Orders"
                                        : "Days"}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <span style={{ marginRight: 5 }}>
                                    {section === "Monetary"
                                      ? "$"
                                      : section === "Frequency"
                                      ? "Orders"
                                      : "Days"}
                                  </span>
                                )
                              }
                              onChange={(value) => {
                                setIsTypingEndValue(true);
                                if (!value) return;

                                const newEndValue = Number(value);
                                const currentTier = tier ?? 0;
                                const nextTier =
                                  section === "Recency"
                                    ? currentTier + 1
                                    : currentTier - 1;

                                const nextTierStartKey = `${rfmType}-${nextTier}-start`;
                                const relatedEndKey =
                                  section === "Recency"
                                    ? `${rfmType}-${nextTier}-end`
                                    : `${rfmType}-${nextTier}-end`;

                                if (!isNaN(newEndValue) && newEndValue > 0) {
                                  const currentTierGroup = Object.values(
                                    RFMTier
                                  )
                                    .filter((val) => typeof val === "number")
                                    .map((t) => ({
                                      startVal: form.getFieldValue(
                                        `${rfmType}-${t}-start`
                                      ),
                                      endVal: form.getFieldValue(
                                        `${rfmType}-${t}-end`
                                      ),
                                    }));

                                  const increment =
                                    section === "Monetary"
                                      ? 0.01
                                      : section === "Frequency"
                                      ? 0.1
                                      : 1;

                                  form.setFieldsValue({
                                    [nextTierStartKey]: newEndValue + increment,
                                  });
                                }

                                form.validateFields([relatedEndKey]);
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  })}

                {section === "Monetary" && (
                  <Row style={{ marginTop: 10 }}>
                    <Col span={24}>
                      <Alert
                        message={<strong>Default Calculation Rules:</strong>}
                        description={
                          <ul style={{ paddingLeft: 20, margin: 0 }}>
                            <li>
                              Average Order Value (AOV) = ${" "}
                              {(
                                defaultSetting?.FullService.find(
                                  (item: any) => item.RFMType === 3
                                )?.RFMEndValue ?? 0
                              ).toLocaleString("en-US", {
                                maximumFractionDigits: 2,
                              })}
                            </li>
                            <li>
                              Default Monetary Value = Default Frequency Value *
                              AOV
                            </li>
                          </ul>
                        }
                        type="info"
                        showIcon
                      />
                    </Col>
                  </Row>
                )}
              </div>
            );
          })}

          <Divider style={{ margin: "12px 0px" }} />
          <Row justify="space-between">
            <Col xs={24} md={12} lg={12}></Col>
            <Col
              xs={24}
              md={12}
              lg={12}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 16,
              }}
            >
              <Button
                key="back"
                onClick={() => onCancel()}
                style={{
                  height: 40,
                  width: isMobile ? "100%" : 150,
                  fontSize: 14,
                }}
              >
                Cancel
              </Button>
              <Button
                key="submit"
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  backgroundColor: "#1565C0",
                  height: 40,
                  width: isMobile ? "100%" : 150,
                  fontSize: 14,
                }}
              >
                Submit
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>
    </>
  );
};

export default CriteriaForm;
